import { useState } from "react";
import { useListPeers } from "@/api/generated/enrollments/enrollments";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { formatDisplayName } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown } from "lucide-react";

interface LearningGroupCardProps {
  enrollmentId: string;
}

export const LearningGroupCard = ({ enrollmentId }: LearningGroupCardProps) => {
  const { data, error, isLoading: loading } = useListPeers(enrollmentId);
  const peers = data?.peers ?? [];
  const currentUserId = data?.currentUserId ?? null;

  const enableCollapsePeers = peers.length > 10;
  const [expanded, setExpanded] = useState(false);
  const COLLAPSED_COUNT = 10;
  const collapsedMaxHeight = `${COLLAPSED_COUNT * 56}px`;

  // The learner's own entry comes first.
  const me = peers.find((peer) => peer.userId === currentUserId);
  const others = peers.filter((peer) => peer.userId !== currentUserId);
  const sortedPeers = me ? [me, ...others] : others;

  if (error) return null;

  if (loading) {
    return (
      <Card className="py-5 gap-4">
        <CardHeader className="pb-0">
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="size-9 rounded-full" />
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (sortedPeers.length === 0) return null;

  return (
    <Card className="py-5 gap-2">
      <CardHeader className="pb-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-bold text-muted-foreground">
            Meine Lerngruppe
          </CardTitle>
          {enableCollapsePeers && (
            <button
              type="button"
              onClick={() => setExpanded((s) => !s)}
              aria-expanded={expanded}
              aria-label={
                expanded ? "Lerngruppe einklappen" : "Lerngruppe ausklappen"
              }
              className="p-2 rounded-full cursor-pointer hover:bg-muted transition-colors"
            >
              <ChevronDown
                size={18}
                className={
                  expanded
                    ? "rotate-180 transition-transform"
                    : "transition-transform"
                }
              />
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent
        className={`flex flex-col gap-1 ${
          enableCollapsePeers && !expanded ? "overflow-y-auto" : ""
        }`}
        style={{
          // eslint-disable-next-line no-nested-ternary
          maxHeight: enableCollapsePeers
            ? expanded
              ? undefined
              : collapsedMaxHeight
            : undefined,
        }}
      >
        {sortedPeers.map((peer, index) => (
          <div
            key={peer.userId}
            className="flex items-center gap-3 py-2.5 rounded-lg"
          >
            <UserAvatar
              firstName={peer.firstName ?? ""}
              lastNameInitial={peer.lastNameInitial ?? undefined}
              colorIndex={index}
              size="md"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground truncate">
                  {formatDisplayName(peer.firstName, peer.lastNameInitial)}
                </span>
                {peer.userId === currentUserId && (
                  <Badge variant="default" className="text-[10px] px-1.5 py-0">
                    Du
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {peer.currentModuleTitle ?? "Noch nicht gestartet"}
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
